<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { toast } from 'dashboard/helper/toast';
import ContactAPI from 'dashboard/api/contacts';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
// Reaproveitados do domínio telephony de propósito — são componentes sem
// dependência de domínio, mover pra um lugar compartilhado é follow-up
// pra quando um terceiro domínio precisar deles.
import ConfirmDialog from '../../telephony/components/ConfirmDialog.vue';
import EmptyState from '../../telephony/components/EmptyState.vue';

const { t } = useI18n();
const store = useStore();

const isLoading = ref(true);
const loadError = ref(false);
// Página pedida por último (currentPage só muda no sucesso): o "Tentar de
// novo" tem que repetir ESTA, não a que estava na tela antes da falha.
const lastRequestedPage = ref(1);
const groups = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);

// Chave estável do grupo (ids dos contatos) — NÃO usar o índice do array:
// ao remover um grupo já mesclado da lista, os índices dos grupos seguintes
// mudam, e uma seleção indexada por posição ficaria associada ao grupo errado.
const groupKey = group => group.contacts.map(c => c.id).join('-');

// groupKey -> id do contato escolhido como "principal" (sobrevive ao merge)
const primarySelection = reactive({});

const fetchGroups = async page => {
  isLoading.value = true;
  loadError.value = false;
  lastRequestedPage.value = page;
  try {
    const {
      data: { payload, meta },
    } = await ContactAPI.duplicateCandidates(page);
    groups.value = payload;
    currentPage.value = meta.current_page;
    totalPages.value = meta.total_pages;
    payload.forEach(group => {
      primarySelection[groupKey(group)] = group.contacts[0]?.id ?? null;
    });
  } catch {
    // Falha da API não pode cair no "nenhum duplicado" — fica um estado de erro.
    loadError.value = true;
    toast.error(t('CONTACT_DUPLICATES.LOAD_ERROR.TITLE'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => fetchGroups(1));

const goToPage = page => {
  if (page < 1 || page > totalPages.value) return;
  fetchGroups(page);
};

const contactLastActivity = contact => {
  if (!contact.last_activity_at)
    return t('CONTACT_DUPLICATES.GROUP.NO_ACTIVITY');
  return new Date(contact.last_activity_at * 1000).toLocaleString();
};

const contactMetaLine = contact => {
  const parts = [
    contact.phone_number,
    contact.email,
    contact.additional_attributes?.company_name,
  ];
  return parts.filter(Boolean).join(' · ');
};

// Grupo + primário escolhido, no momento em que o admin confirma a mesclagem
const confirmDialog = ref(null);
const pendingMerge = ref(null);

const openConfirm = group => {
  const primaryId = primarySelection[groupKey(group)];
  const primaryContact = group.contacts.find(c => c.id === primaryId);
  const otherContacts = group.contacts.filter(c => c.id !== primaryId);
  if (!primaryContact || otherContacts.length === 0) return;

  pendingMerge.value = { key: groupKey(group), primaryContact, otherContacts };
  confirmDialog.value?.open();
};

const performMerge = async () => {
  const { key, primaryContact, otherContacts } = pendingMerge.value;

  const promise = otherContacts.reduce(
    (chain, mergeeContact) =>
      chain.then(() =>
        store.dispatch('contacts/merge', {
          childId: mergeeContact.id,
          parentId: primaryContact.id,
        })
      ),
    Promise.resolve()
  );

  toast.promise(promise, {
    loading: t('CONTACT_DUPLICATES.TOAST.MERGING'),
    success: () => {
      groups.value = groups.value.filter(group => groupKey(group) !== key);
      delete primarySelection[key];
      return t('CONTACT_DUPLICATES.TOAST.SUCCESS');
    },
    error: () => t('CONTACT_DUPLICATES.TOAST.ERROR'),
  });

  confirmDialog.value?.close();
  pendingMerge.value = null;
};
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-copy-x"
        accent="ruby"
        :title="t('CONTACT_DUPLICATES.PAGE_TITLE')"
        :subtitle="t('CONTACT_DUPLICATES.PAGE_SUBTITLE')"
      />
    </template>

    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-10" />
      <span class="ml-3 text-sm text-n-slate-11">{{
        t('CONTACT_DUPLICATES.LOADING')
      }}</span>
    </div>

    <EmptyState
      v-else-if="loadError"
      :title="t('CONTACT_DUPLICATES.LOAD_ERROR.TITLE')"
      :description="t('CONTACT_DUPLICATES.LOAD_ERROR.DESCRIPTION')"
      icon="i-lucide-circle-alert"
    >
      <template #action>
        <Button
          :label="t('CONTACT_DUPLICATES.LOAD_ERROR.RETRY')"
          size="sm"
          @click="fetchGroups(lastRequestedPage)"
        />
      </template>
    </EmptyState>

    <EmptyState
      v-else-if="groups.length === 0"
      :title="t('CONTACT_DUPLICATES.EMPTY_STATE.TITLE')"
      :description="t('CONTACT_DUPLICATES.EMPTY_STATE.DESCRIPTION')"
      icon="i-lucide-user-check"
    />

    <div v-else class="flex flex-col gap-4">
      <div
        v-for="group in groups"
        :key="groupKey(group)"
        class="rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div class="px-4 py-2 border-b border-n-weak text-xs text-n-slate-10">
          {{ t('CONTACT_DUPLICATES.GROUP.PICK_PRIMARY_HINT') }}
        </div>
        <div
          v-for="contact in group.contacts"
          :key="contact.id"
          class="flex items-center gap-4 px-4 py-3 border-b border-n-weak last:border-b-0"
        >
          <input
            :id="`primary-${groupKey(group)}-${contact.id}`"
            v-model="primarySelection[groupKey(group)]"
            type="radio"
            :name="`primary-${groupKey(group)}`"
            :value="contact.id"
            class="size-4"
          />
          <label
            :for="`primary-${groupKey(group)}-${contact.id}`"
            class="flex-1 min-w-0 cursor-pointer"
          >
            <div class="flex items-center gap-2">
              <span class="font-medium text-n-slate-12">{{
                contact.name
              }}</span>
              <span
                v-if="primarySelection[groupKey(group)] === contact.id"
                class="text-xs px-1.5 py-0.5 rounded bg-n-teal-3 text-n-teal-11"
              >
                {{ t('CONTACT_DUPLICATES.GROUP.TO_BE_KEPT') }}
              </span>
              <span
                v-else
                class="text-xs px-1.5 py-0.5 rounded bg-n-ruby-3 text-n-ruby-11"
              >
                {{ t('CONTACT_DUPLICATES.GROUP.TO_BE_DELETED') }}
              </span>
            </div>
            <div class="text-sm text-n-slate-11">
              {{ contactMetaLine(contact) }}
            </div>
            <div class="text-xs text-n-slate-10">
              {{ contactLastActivity(contact) }}
            </div>
          </label>
        </div>
        <div class="px-4 py-3 flex justify-end">
          <Button
            icon="i-lucide-merge"
            color="ruby"
            size="sm"
            @click="openConfirm(group)"
          >
            {{ t('CONTACT_DUPLICATES.GROUP.MERGE_BUTTON') }}
          </Button>
        </div>
      </div>

      <div
        v-if="totalPages > 1"
        class="flex items-center justify-center gap-4 py-2"
      >
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :disabled="currentPage <= 1"
          @click="goToPage(currentPage - 1)"
        >
          {{ t('CONTACT_DUPLICATES.PAGINATION.PREVIOUS') }}
        </Button>
        <span class="text-sm text-n-slate-11">
          {{
            t('CONTACT_DUPLICATES.PAGINATION.PAGE_OF', {
              current: currentPage,
              total: totalPages,
            })
          }}
        </span>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :disabled="currentPage >= totalPages"
          @click="goToPage(currentPage + 1)"
        >
          {{ t('CONTACT_DUPLICATES.PAGINATION.NEXT') }}
        </Button>
      </div>
    </div>

    <ConfirmDialog
      ref="confirmDialog"
      :title="t('CONTACT_DUPLICATES.CONFIRM.TITLE')"
      :message="
        pendingMerge
          ? t('CONTACT_DUPLICATES.CONFIRM.MESSAGE', {
              mergeeName: pendingMerge.otherContacts
                .map(c => c.name)
                .join(', '),
              primaryName: pendingMerge.primaryContact.name,
            })
          : ''
      "
      :confirm-label="t('CONTACT_DUPLICATES.CONFIRM.CONFIRM_LABEL')"
      :cancel-label="t('CONTACT_DUPLICATES.CONFIRM.CANCEL_LABEL')"
      confirm-color="ruby"
      @confirm="performMerge"
    />
  </PageLayout>
</template>
